import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import media from 'styled-media-query';
import styled from 'styled-components';
import sort from 'fast-sort';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import { State } from 'src/state/ducks';
import * as NotificationActions from 'src/state/ducks/notification/actions';
import { formatDateToDate } from 'src/helpers/UtilityHelper';

import StyleConfig from 'src/constants/StyleConfig';
import { DefaultPageProps, HistoryProps } from 'src/constants/Interface';
import { UserRole } from 'src/firebase/db/Admins';

import { DefaultLayout } from 'src/components/layouts/DefaultLayout';
import Heading from 'src/components/atoms/typo/Heading';
import Button, { LinkButton } from 'src/components/atoms/button/Button';
import PlainHomeButton from 'src/components/atoms/button/PlainHomeButton';
import SpinnerModal from 'src/components/organisms/loading/SpinnerModal';
import ReservationList from 'src/components/organisms/list/ReservationList';
import ConfirmModal from 'src/components/organisms/modal/ConfirmModal';

type OwnProps = {
  id: string;
  notification_template_ids: string;
};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const ReservationIndexPage = (props: Props) => {
  const {
    login_shop_id,
    getReservations,
    deleteReservation,
    duplicateReservations,
    isFetching,
    isAdmin,
    reservations,
    history,
  } = props;

  // パンの選択状態
  const [selectedNotificationIds, setSelectedNotificationIds] = useState<{ selected: boolean, id: string }[]>([]);

  // 初期表示：予約情報の取得
  useEffect(() => {
    getReservations(login_shop_id);
  }, [getReservations, login_shop_id]);

  // 初期表示：予約情報選択状態の初期値セット
  useEffect(() => {
    if (reservations.length) {
      const values = reservations.map(({ id }) => ({
        id,
        selected: false,
      }));
      setSelectedNotificationIds(values);
    }
  }, [reservations, setSelectedNotificationIds]);

  // 予約通知編集画面へ
  const onEdit = useCallback(
    (notificationId: string) => {
      history.push(`/reservations/${notificationId}/edit`);
    },
    [history],
  );

  // 削除完了モーダル
  const onCompleteDelete = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="下書きの破棄が完了しました"
          contentText="下書きを破棄しました。"
          onClose={onClose}
          closeText="とじる"
          color="green"
        />
      ),
    });
  };

  // 削除確認モーダル
  const onDelete = useCallback(
    async (id: string) => {
      confirmAlert({
        customUI: ({ onClose }) => (
          <ConfirmModal
            title="下書きの破棄"
            contentText="下書きの内容が破棄されますが、よろしいでしょうか？"
            closeText="キャンセル"
            onClose={onClose}
            submitText="破棄する"
            onSubmit={async () => {
              deleteReservation(id);
              onClose();
              onCompleteDelete();
            }}
            color="red"
          />
        ),
      });
    },
    [deleteReservation],
  );

  // 選択状態の変更
  const onSelect = useCallback((id: string) => {
    setSelectedNotificationIds((prev) => {
      const values = prev.map(v => ({
        ...v,
        selected: v.id === id ? !v.selected : v.selected,
      }))
      return values;
    })
  }, [setSelectedNotificationIds]);

  // 選択状態のデータを複製する
  const onDuplicate = useCallback(() => {
    const values = selectedNotificationIds
    .filter(v => v.selected)
    .map(v => v.id);
    duplicateReservations(values);
  }, [selectedNotificationIds, duplicateReservations]);

  // 複製ボタンの状態
  const isDisabledDuplicateButton = useMemo(() =>
    selectedNotificationIds.filter(v => v.selected).length === 0
  , [selectedNotificationIds]);

  // 複製ボタンのテキスト
  const duplicateButtonText = useMemo(() =>
    isDisabledDuplicateButton ? 'アイテムを選択してください' : '選択したアイテムを複製する'
  , [isDisabledDuplicateButton]);

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <div className="p-ReservationIndexPage">
          <Heading tag="h1" text={`事前予約注文を受けるアイテムを設定してください`} />

          <ReservationList
            notifications={reservations}
            selectedIds={selectedNotificationIds}
            isAdmin={isAdmin}
            onEdit={onEdit}
            onDelete={onDelete}
            onSelect={onSelect}
          />

          <ButtonWrapper>
            <Button
            type="button"
            isDisabled={isDisabledDuplicateButton}
            buttonColor="green"
            clickEvent={() => onDuplicate()}
            text={duplicateButtonText}
          />
          </ButtonWrapper>

          <ButtonWrapper>
            <LinkButton
              isDisabled={false}
              buttonColor="green"
              url="/reservations/create"
              text="新しく登録する"
            />
          </ButtonWrapper>
          <div className="item">
            <PlainHomeButton />
          </div>
        </div>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  .p-ReservationIndexPage {
    > .item {
      margin: ${StyleConfig.HELPER.M24} auto;
    }
  }
`;

const ButtonWrapper = styled.div`
  margin: 24px 0;
  padding: 16px;
  background-color: #fff;

  ${media.lessThan('small')`
    width: 100vw;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  `}
`;

const mapStateToProps = (state: State) => {
  const {
    notification: {
      reservations: { resource, isFetching },
    },
    admin: {
      user: { role },
    },
  } = state;
  const reservations = sort(resource).by([
    { asc: ({ updated_at }) => (updated_at ? formatDateToDate(updated_at).getTime() : false) },
  ]);
  return {
    reservations,
    isFetching,
    isAdmin: role === UserRole.ADMIN,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getReservations: (shopId: string) => dispatch(NotificationActions.getReservations({ shopId })),
  deleteReservation: (id: string) => dispatch(NotificationActions.deleteReservation(id)),
  duplicateReservations: (ids: string[]) => dispatch(NotificationActions.duplicateReservations(ids)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReservationIndexPage);
